import React from 'react';
import { AppSidebar } from 'bk-web-app/app-sidebar';
import { JournalEntrySidebar } from 'journal-entry-sidebar';
import Transactions1099View from '../transactions-1099-view/Transactions1099View';
import { ReactElement } from 'react';
import { SelectedDataEntriesProvider } from 'general-ledger';
import { WrappedSpinner } from 'spinner';
import { useV2Query } from 'utils';
import { GET_JOURNAL_ENTRIES_1099 } from './graphql';
import { NullState } from 'bench-internal-theme';
import compile1099ViewData from '../compile1099ViewData';
import { useClient } from 'bk-web-app/ClientContext';
import { DateRange } from 'general-ledger/types';

type Transactions1099ContainerProps = {
  dateRange: DateRange;
  search: string;
};

const Transactions1099Container = ({
  search,
  dateRange,
}: Transactions1099ContainerProps): ReactElement => {
  const client = useClient();
  const { id: clientId } = client;
  const year = new Date(dateRange.endDate || '').getFullYear();
  const { data, loading, error } = useV2Query(GET_JOURNAL_ENTRIES_1099, {
    variables: {
      input: {
        clientId,
        year,
      },
    },
    skip: !clientId || !year,
  });
  if (!year || year < 2022) {
    return (
      <NullState
        bigEmoji="🙈"
        description="Uh oh! There seems to be a problem, sorry!"
        title="We currently don't support 1099 Transactions for years earlier than 2022."
      />
    );
  }
  if (loading) {
    return <WrappedSpinner />;
  }
  if (error || !client) {
    return (
      <NullState
        bigEmoji="🙈"
        description="Uh oh! There seems to be a problem, sorry!"
        title="There was a problem loading data for the 1099 Transactions Tab."
      />
    );
  }
  if (data?.journalEntries1099?.length === 0) {
    return (
      <NullState
        bigEmoji="🤩"
        description="Great job keeping tidy books."
        title="You have no 1099 transactions!"
      />
    );
  }
  const { dataEntriesMap } = compile1099ViewData(data.journalEntries1099);

  return (
    <SelectedDataEntriesProvider>
      <AppSidebar>
        {({ closeSidebar, openSidebar, openerMeta }) => {
          const selectedDataEntry =
            // eslint-disable-next-line no-underscore-dangle
            openerMeta && openerMeta.__typename === 'DataEntry' ? openerMeta : undefined;
          const toggleDataEntry = dataEntry => {
            if (dataEntry && dataEntry !== openerMeta) {
              openSidebar(
                <JournalEntrySidebar
                  dismiss={closeSidebar}
                  journalEntryId={dataEntry.journalEntry.id}
                  key={dataEntry.journalEntry.id}
                />,
                dataEntry,
              );
            } else {
              closeSidebar();
            }
          };
          return (
            <Transactions1099View
              search={search}
              transactions1099Entries={Array.from(dataEntriesMap.values())}
              selectedDataEntry={selectedDataEntry}
              toggleDataEntry={toggleDataEntry}
              client={client}
              availableDataEntriesMap={dataEntriesMap}
              dateRange={dateRange}
            />
          );
        }}
      </AppSidebar>
    </SelectedDataEntriesProvider>
  );
};

export default Transactions1099Container;
